import type { AxiosError } from 'axios';
import { authClient } from './client';
import type {
  ConsignorSaleItem,
  ConsignorSalesPayload,
  ConsignorSalesSummary,
  PayoutChannel,
  PendingPaymentInfo,
  SalePaymentStatus,
} from '../types/consignorSales';

// ─── Parsers ──────────────────────────────────────────────────────────────────

const VALID_PAYMENT_STATUSES: SalePaymentStatus[] = ['pending', 'scheduling', 'scheduled', 'paid'];
const VALID_CHANNELS: PayoutChannel[] = ['cash', 'store_credit', 'transfer'];

function parseSaleItem(raw: unknown): ConsignorSaleItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id) || id <= 0) return null;

  const artIdRaw = r.artId ?? r.art_id;
  const imageUrlRaw = r.imageUrl ?? r.image_url ?? r.thumbnailUrl;
  const soldAtRaw = r.soldAt ?? r.sold_at ?? r.createdAt ?? r.created_at;
  const scheduledDateRaw = r.paymentScheduledDate ?? r.payment_scheduled_date ?? r.scheduledDate;
  const paymentStatusRaw = String(r.paymentStatus ?? r.payment_status ?? 'pending');
  const paymentStatus: SalePaymentStatus = VALID_PAYMENT_STATUSES.includes(
    paymentStatusRaw as SalePaymentStatus
  )
    ? (paymentStatusRaw as SalePaymentStatus)
    : 'pending';

  return {
    id,
    artId: typeof artIdRaw === 'string' && artIdRaw.trim() ? artIdRaw.trim() : null,
    productName: String(r.productName ?? r.product_name ?? '').trim(),
    variantName: String(r.variantName ?? r.variant_name ?? '').trim(),
    imageUrl: typeof imageUrlRaw === 'string' && imageUrlRaw.trim() ? imageUrlRaw.trim() : null,
    soldAt: typeof soldAtRaw === 'string' && soldAtRaw ? soldAtRaw : new Date(0).toISOString(),
    payoutAmount: Number(r.payoutAmount ?? r.payout_amount ?? 0),
    paymentStatus,
    paymentScheduledDate:
      typeof scheduledDateRaw === 'string' && scheduledDateRaw ? scheduledDateRaw : null,
  };
}

function parsePendingPayment(raw: unknown): PendingPaymentInfo | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const channelRaw = String(r.channel ?? '').toLowerCase();
  const channel: PayoutChannel = VALID_CHANNELS.includes(channelRaw as PayoutChannel)
    ? (channelRaw as PayoutChannel)
    : 'cash';
  const scheduledDateRaw = r.scheduledDate ?? r.scheduled_date;
  return {
    id,
    amount: Number(r.amount ?? 0),
    channel,
    scheduledDate:
      typeof scheduledDateRaw === 'string' && scheduledDateRaw ? scheduledDateRaw : null,
  };
}

function parseSummary(raw: unknown): ConsignorSalesSummary {
  const empty: ConsignorSalesSummary = {
    pendingAmount: 0,
    hasPendingPayment: false,
    pendingPayment: null,
  };
  if (!raw || typeof raw !== 'object') return empty;
  const r = raw as Record<string, unknown>;
  const pendingAmount = Number(r.pendingAmount ?? r.pending_amount ?? 0);
  const hasPendingPayment = Boolean(r.hasPendingPayment ?? r.has_pending_payment ?? false);
  const pendingPayment = parsePendingPayment(r.pendingPayment ?? r.pending_payment);
  return {
    pendingAmount: Number.isFinite(pendingAmount) ? pendingAmount : 0,
    hasPendingPayment,
    pendingPayment,
  };
}

// ─── Exports ──────────────────────────────────────────────────────────────────

export type ConsignorSalesResult =
  | { ok: true; data: ConsignorSalesPayload }
  | { ok: false; error: string };

export type SalesFilter = 'all' | 'pending' | 'scheduling' | 'scheduled' | 'paid';

/**
 * GET /api/consignor/sales
 *
 * Returns the current consignor's sold inventory items with payment status,
 * plus a summary for the payment hero card.
 *
 * Query params (all optional):
 *   status  – 'pending' | 'scheduling' | 'scheduled' | 'paid'  (filter by payment status)
 *   page    – default 1
 *   pageSize – default 30
 *
 * Response 200 JSON:
 * {
 *   "message": "OK",
 *   "data": {
 *     "items": [
 *       {
 *         "id": 1,
 *         "artId": "42",
 *         "productName": "Air Jordan 1",
 *         "variantName": "9 US · Deadstock",
 *         "imageUrl": "https://...",
 *         "soldAt": "2024-01-15T00:00:00Z",
 *         "payoutAmount": 1750.00,     // soldAmount * (1 - commission/100), rounded 2dp
 *         "paymentStatus": "pending" | "scheduling" | "scheduled" | "paid",
 *         "paymentScheduledDate": "2024-01-20T00:00:00Z" // null for pending/scheduling
 *       }
 *     ],
 *     "total": 12,
 *     "summary": {
 *       "pendingAmount": 5200.00,       // sum of payoutAmount for status=pending sales
 *       "hasPendingPayment": false,    // true if there is an open (unpaid) payment batch
 *       "pendingPayment": null         // | { id, amount, channel, scheduledDate }
 *     }
 *   }
 * }
 *
 * Notes:
 *   - payoutAmount must NEVER expose publicPrice or commission rate.
 *   - Items ordered by soldAt DESC.
 *
 * Errors: 403, 500.
 */
export async function fetchConsignorSales(
  filter: SalesFilter = 'all',
  page = 1
): Promise<ConsignorSalesResult> {
  try {
    const params: Record<string, string | number> = { page, pageSize: 30 };
    if (filter !== 'all') params.status = filter;
    const { data } = await authClient.get<unknown>('/api/consignor/sales', { params });
    const body = data as Record<string, unknown>;
    const inner = (typeof body.data === 'object' && body.data ? body.data : body) as Record<
      string,
      unknown
    >;
    const rawItems = inner.items;
    const items = Array.isArray(rawItems)
      ? rawItems.map(parseSaleItem).filter((x): x is ConsignorSaleItem => x !== null)
      : [];
    return {
      ok: true,
      data: {
        items,
        total: typeof inner.total === 'number' ? inner.total : items.length,
        summary: parseSummary(inner.summary),
      },
    };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo cargar tu historial de ventas.',
    };
  }
}

export type CreatePaymentResult = { ok: true; paymentId: number } | { ok: false; error: string };

/**
 * POST /api/consignor/payments
 *
 * Creates a new payment request for the current consignor, grouping all
 * completed sales with paymentId: null (status=pending) into a single batch.
 * Admins will later assign a scheduled date and mark it as paid.
 *
 * Request body JSON:
 * {
 *   "channel": "cash" | "store_credit" | "transfer"
 * }
 *
 * Response 200 JSON:
 * {
 *   "message": "OK",
 *   "data": { "paymentId": 42 }
 * }
 *
 * Errors:
 *   400 – invalid channel value.
 *   403 – not authenticated.
 *   409 – consignor already has an open (unpaid) payment batch; close it first.
 *   422 – no pending (paymentId: null) completed sales to batch.
 *   500 – server error.
 */
export async function createConsignorPayment(channel: PayoutChannel): Promise<CreatePaymentResult> {
  try {
    const { data } = await authClient.post<unknown>('/api/consignor/payments', { channel });
    const body = data as Record<string, unknown>;
    const inner = (typeof body.data === 'object' && body.data ? body.data : body) as Record<
      string,
      unknown
    >;
    const paymentId = Number(inner.paymentId ?? inner.id ?? 0);
    return { ok: true, paymentId };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo crear la solicitud de pago.',
    };
  }
}
